import {Component, Object3D, PhysXComponent} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {vec3} from 'gl-matrix';
import {PlayerMovementHandler} from './player-movement-handler.js';

const headWorld = vec3.create();
const bodyWorld = vec3.create();
const correctionDir = vec3.create();

/**
 * vr-body-follow
 *
 * Keeps the player's physics body close to the VR head by pushing movement through
 * PlayerMovementHandler. This avoids direct PhysX teleports while reducing visible
 * head/body divergence in room-scale play.
 */
export class VrBodyFollow extends Component {
    static TypeName = 'vr-body-follow';

    @property.object()
    playerObject: Object3D | null = null; // must have player-movement-handler

    @property.object({required: true})
    headObject!: Object3D;

    @property.object()
    trackedSpaceObject: Object3D | null = null;

    @property.object()
    bodyObject: Object3D | null = null; // defaults to playerPhysXObject from handler

    @property.bool(true)
    activeInXR = true;

    @property.bool(false)
    activeOutXR = false;

    @property.bool(true)
    lockY = true;

    @property.float(0.2)
    deadzoneMeters = 0.2;

    @property.float(0.14)
    settleMeters = 0.14;

    @property.float(0.45)
    hardLimitMeters = 0.45;

    @property.float(800.0)
    correctionAcceleration = 800.0;

    @property.float(260.0)
    maxCorrectionPerSecond = 260.0;

    @property.float(1.8)
    velocityDamping = 1.8;

    private playerMovementHandler: PlayerMovementHandler | null = null;
    private bodyPhysXComp: PhysXComponent | null = null;
    private correcting = false;

    start() {
        const tempPlayerMovement =
            this.playerObject?.getComponent(PlayerMovementHandler) ??
            this.object.getComponent(PlayerMovementHandler);

        if (!tempPlayerMovement) {
            throw new Error(
                'The vr-body-follow component requires a player-movement-handler component on playerObject (or on this object if playerObject is unset).'
            );
        }

        this.playerMovementHandler = tempPlayerMovement;
        const bodyRef =
            this.bodyObject ?? this.playerMovementHandler.playerPhysXObject ?? this.object;
        this.bodyPhysXComp = bodyRef.getComponent(PhysXComponent) ?? null;
    }

    update(dt: number) {
        if (dt <= 0 || !this.playerMovementHandler) {
            return;
        }

        const xrActive = !!(this.engine.xr && this.engine.xr.session);
        if ((xrActive && !this.activeInXR) || (!xrActive && !this.activeOutXR)) {
            return;
        }

        const bodyRef =
            this.bodyObject ?? this.playerMovementHandler.playerPhysXObject ?? this.object;

        this.headObject.getPositionWorld(headWorld);
        bodyRef.getPositionWorld(bodyWorld);

        vec3.sub(correctionDir, headWorld, bodyWorld);
        if (this.lockY) {
            correctionDir[1] = 0;
        }

        const distance = vec3.length(correctionDir);
        const enterDistance = this.deadzoneMeters;
        const exitDistance = Math.min(this.settleMeters, this.deadzoneMeters);

        if (!this.correcting) {
            this.correcting = distance > enterDistance;
        } else if (distance <= exitDistance) {
            this.correcting = false;
        }

        if (!this.correcting || distance < 1e-5) {
            return;
        }

        const denominator = Math.max(1e-4, this.hardLimitMeters - this.deadzoneMeters);
        const gain = Math.min(1, Math.max(0, (distance - this.deadzoneMeters) / denominator));

        vec3.scale(correctionDir, correctionDir, 1 / distance);

        let magnitude = this.correctionAcceleration * gain * dt;

        if (this.bodyPhysXComp) {
            const velocityAlongCorrection = vec3.dot(
                this.bodyPhysXComp.linearVelocity,
                correctionDir
            );
            // Reduce correction when already moving toward the head to avoid overshoot.
            magnitude -= velocityAlongCorrection * this.velocityDamping * dt;
            if (magnitude < 0) {
                magnitude = 0;
            }
        }

        const maxFrameMagnitude = this.maxCorrectionPerSecond * dt;
        if (magnitude > maxFrameMagnitude) {
            magnitude = maxFrameMagnitude;
        }

        vec3.scale(correctionDir, correctionDir, magnitude);

        if (this.trackedSpaceObject) {
            // Counter-translate tracked space so body catch-up does not drag head world pose with it.
            this.trackedSpaceObject.translateWorld([
                -correctionDir[0],
                -correctionDir[1],
                -correctionDir[2],
            ]);
        }

        this.playerMovementHandler.requestMovementAdditive(correctionDir);
    }
}